import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import Sheet from '@amzn/meridian/sheet';
import Text from '@amzn/meridian/text';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import {
  handleOpenToaster,
  MixPanel,
  MixPanelEventName,
  ScreenSize,
  SocialMediaModal,
  toasterData,
} from '@amzn/rangoli-seller-desktop-utils/src/index';
import { observer } from 'mobx-react-lite';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';

import { ProfileMenuInfo } from '../../constants/ProfileMenu';
import { userAuthStore } from '../../store/UserAuthStore';
import { italicStyle } from '../../Style/Style';
import { IShareStoreModal } from '../../types/CatalogueListingTypes';

const {
  QR_CODE_MESSAGE,
  DOWNLOAD_QR_CODE,
  QR_CODE_ERROR,
  SHARE,
  SHARE_LINK,
  SHARE_QR_CODE,
  VISIT_ONLINE_STORE,
  QR_FILE_NAME,
  QR_BLOB_FILE_TYPE,
  QR_CREATE_ELEMENT,
  QR_DOCUMENT_ID,
} = ProfileMenuInfo;

const { SHARE_STORE_BUTTON_CLICK, SHARE_STORE_LINK } = MixPanelEventName;

const ShareStoreModal = ({
  isShareStoreOpen,
  handleOnCloseShareStore,
  domainLink,
}: IShareStoreModal) => {
  const [isSocialMediaLinkOpen, setIsSocialMediaLinkOpen] = useState<boolean>(false);
  const handleCloseSocialMediaLink = (): void => setIsSocialMediaLinkOpen(false);
  const { currentStoreDetails } = userAuthStore;

  const downloadQR = (): void => {
    const svgElement = document.getElementById(QR_DOCUMENT_ID);
    if (!svgElement) {
      handleOpenToaster({ ...toasterData, text: QR_CODE_ERROR });
      return;
    }
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: QR_BLOB_FILE_TYPE });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement(QR_CREATE_ELEMENT);
    downloadLink.href = svgUrl;
    downloadLink.download = QR_FILE_NAME;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  };

  const shareStoreButtonClick = () => {
    setIsSocialMediaLinkOpen(true);
    MixPanel.track(SHARE_STORE_BUTTON_CLICK, { store_url: domainLink });
  };

  const shareUrlClick = (): void => {
    MixPanel.track(SHARE_STORE_LINK, { store_url: domainLink });
    window.open(domainLink, '_blank');
  };

  const handleViewShareStore = (showShareButtonFullWidth: boolean): React.JSX.Element => {
    return (
      <Column width="100%">
        <Heading level={6}>{SHARE_LINK}</Heading>
        <Row
          alignmentHorizontal={showShareButtonFullWidth ? 'center' : 'justify'}
          alignmentVertical="center"
          wrap="down"
          spacingInset="300"
        >
          <Link onClick={shareUrlClick} type="secondary">
            {domainLink}
          </Link>
          <Button
            size="small"
            onClick={shareStoreButtonClick}
            minWidth={showShareButtonFullWidth && '60%'}
          >
            {SHARE}
          </Button>
        </Row>
        <Heading level={6}>{SHARE_QR_CODE}</Heading>
        <Text type="b200" className={italicStyle}>
          {QR_CODE_MESSAGE}
        </Text>
        <Column width="100%" alignmentHorizontal="center" spacing="none">
          <Text type="b200">{currentStoreDetails?.storeName}</Text>
          <QRCodeSVG value={domainLink} id={QR_DOCUMENT_ID} includeMargin={true} />
          <Text type="b200">{VISIT_ONLINE_STORE}</Text>
        </Column>
        <Row width="100%" alignmentHorizontal="center" alignmentVertical="center">
          <Link onClick={downloadQR} type="secondary">
            {DOWNLOAD_QR_CODE}
          </Link>
        </Row>
      </Column>
    );
  };

  return (
    <>
      <Responsive
        query="max-width"
        props={{
          showMobileView: {
            default: false,
            [ScreenSize._720]: true,
          },
          showShareButtonFullWidth: {
            default: false,
            [ScreenSize._425]: true,
          },
        }}
      >
        {({ showMobileView, showShareButtonFullWidth }) =>
          !showMobileView ? (
            <Modal
              open={isShareStoreOpen}
              onClose={handleOnCloseShareStore}
              width="450px"
              bodySpacingInset="400 none 400 400"
            >
              {handleViewShareStore(showShareButtonFullWidth)}
            </Modal>
          ) : (
            <Sheet
              type="overlay"
              side="bottom"
              open={isShareStoreOpen}
              onClose={handleOnCloseShareStore}
            >
              <Row alignmentHorizontal="end" width="100%">
                <Button onClick={handleOnCloseShareStore} type="icon">
                  <Icon tokens={closeSmallTokens}></Icon>
                </Button>
              </Row>
              {handleViewShareStore(showShareButtonFullWidth)}
            </Sheet>
          )
        }
      </Responsive>
      <SocialMediaModal
        isSocialMediaLinkOpen={isSocialMediaLinkOpen}
        handleCloseSocialMediaLink={handleCloseSocialMediaLink}
        domainLink={domainLink}
        shareModalTitle={SHARE_LINK}
        mobileNumber={Number(currentStoreDetails?.mobileNumber)}
        storeName={currentStoreDetails?.storeName}
      />
    </>
  );
};

export default observer(ShareStoreModal);
